<template>
  <b-card no-body class="border mb-2">
    <b-card-body class="pb-1">
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <h5 class="mb-0">Request log</h5>
          <small class="text-muted">
            Individual calls, newest first. Routine successes are kept
            {{ retentionNote }}.
          </small>
        </div>

        <b-form-checkbox v-model="errorsOnly" switch class="mt-50 mt-md-0">
          Failures only
        </b-form-checkbox>
      </div>

      <b-row class="mt-1">
        <b-col cols="6" md="3" class="mb-50">
          <b-form-input
            v-model="filters.route"
            size="sm"
            placeholder="Endpoint, e.g. members/:id"
            debounce="500"
          />
        </b-col>
        <b-col cols="6" md="2" class="mb-50">
          <b-form-select v-model="filters.method" size="sm" :options="methodOptions" />
        </b-col>
        <b-col cols="6" md="2" class="mb-50">
          <b-form-select v-model="filters.outcome" size="sm" :options="outcomeOptions" />
        </b-col>
        <b-col cols="6" md="2" class="mb-50">
          <b-form-input
            v-model="filters.min_duration_ms"
            size="sm"
            type="number"
            min="0"
            placeholder="Slower than (ms)"
            debounce="500"
          />
        </b-col>
      </b-row>
    </b-card-body>

    <b-table
      :items="rows"
      :fields="fields"
      :busy="loading"
      responsive
      show-empty
      empty-text="No matching calls in this window."
      class="mb-0 usage-logs-table"
    >
      <template #table-busy>
        <div class="text-center text-muted py-2">
          <b-spinner small class="mr-50" />
          Loading…
        </div>
      </template>

      <template #cell(timestamp)="data">
        <div>{{ formatTime(data.item.timestamp) }}</div>
        <small class="text-muted">{{ formatDay(data.item.timestamp) }}</small>
      </template>

      <template #cell(route)="data">
        <b-badge pill :variant="`light-${methodVariant(data.item.method)}`" class="mr-50">
          {{ data.item.method }}
        </b-badge>
        <code class="text-body">{{ data.item.route }}</code>
        <div v-if="data.item.error" class="small text-danger">{{ data.item.error }}</div>
      </template>

      <template #cell(status_code)="data">
        <b-badge pill :variant="`light-${statusVariant(data.item.status_code)}`">
          {{ data.item.status_code }}
        </b-badge>
        <div class="small text-muted text-capitalize">{{ outcomeLabel(data.item.outcome) }}</div>
      </template>

      <template #cell(duration_ms)="data">
        {{ formatDuration(data.item.duration_ms) }}
      </template>

      <template #cell(result_count)="data">
        {{ data.item.result_count == null ? '—' : formatCount(data.item.result_count) }}
      </template>

      <template #cell(details)="data">
        <b-button size="sm" variant="flat-secondary" @click="data.toggleDetails">
          <feather-icon :icon="data.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
        </b-button>
      </template>

      <template #row-details="data">
        <div class="px-2 pb-1">
          <b-row>
            <b-col v-for="detail in detailsFor(data.item)" :key="detail.label" cols="6" md="3" class="mb-50">
              <small class="text-muted d-block">{{ detail.label }}</small>
              <span class="font-small-3">{{ detail.value }}</span>
            </b-col>
          </b-row>
        </div>
      </template>
    </b-table>

    <b-card-body v-if="nextCursor || rows.length" class="text-center py-1">
      <b-button
        v-if="nextCursor"
        size="sm"
        variant="outline-primary"
        :disabled="loading"
        @click="fetch(true)"
      >
        Load more
      </b-button>
      <small v-else class="text-muted">End of the log for this window.</small>
    </b-card-body>
  </b-card>
</template>

<script>
import { get } from 'lodash'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTable,
  BBadge,
  BButton,
  BSpinner,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  formatBytes,
  formatCount,
  formatDuration,
  methodVariant,
  outcomeLabel,
  statusVariant,
} from './usageFormat'

/**
 * The forensic view: individual calls.
 *
 * Paged on an opaque cursor rather than page numbers — the underlying log is ordered by
 * `_id`, which is also its timestamp, so "load more" walks the index backwards at
 * constant cost however deep it goes. Skipping to page 400 is deliberately not offered:
 * it would be the one expensive query in this whole surface.
 *
 * Filters are applied server-side. Changing one restarts paging from the top, since a
 * cursor is only meaningful within the filter set that produced it.
 */
export default {
  name: 'UsageLogsTable',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTable,
    BBadge,
    BButton,
    BSpinner,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
  },
  props: {
    clientId: {
      type: String,
      required: true,
    },
    window: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      rows: [],
      loading: false,
      nextCursor: null,
      errorsOnly: false,

      filters: {
        route: '',
        method: null,
        outcome: null,
        min_duration_ms: '',
      },

      methodOptions: [
        { value: null, text: 'Any method' },
        { value: 'GET', text: 'GET' },
        { value: 'POST', text: 'POST' },
        { value: 'PUT', text: 'PUT' },
        { value: 'DELETE', text: 'DELETE' },
      ],

      outcomeOptions: [
        { value: null, text: 'Any outcome' },
        { value: 'unauthenticated', text: 'Unauthenticated' },
        { value: 'forbidden', text: 'Forbidden' },
        { value: 'not_found', text: 'Not found' },
        { value: 'bad_request', text: 'Bad request' },
        { value: 'rate_limited', text: 'Rate limited' },
        { value: 'server_error', text: 'Server error' },
      ],

      fields: [
        { key: 'timestamp', label: 'When', class: 'width-150' },
        { key: 'route', label: 'Call' },
        { key: 'status_code', label: 'Status' },
        { key: 'duration_ms', label: 'Took', class: 'text-right' },
        { key: 'result_count', label: 'Records', class: 'text-right' },
        { key: 'details', label: '', class: 'text-right' },
      ],
    }
  },
  computed: {
    retentionNote() {
      return 'for two weeks, failures for six'
    },
    /** One value the watcher can observe, so any filter change restarts paging. */
    queryFingerprint() {
      return JSON.stringify({
        window: this.window,
        errorsOnly: this.errorsOnly,
        ...this.filters,
      })
    },
  },
  watch: {
    queryFingerprint: {
      handler() {
        this.fetch(false)
      },
      immediate: true,
    },
  },
  methods: {
    formatCount,
    formatDuration,
    methodVariant,
    outcomeLabel,
    statusVariant,
    formatTime(value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString()
    },
    formatDay(value) {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
    },
    /**
     * The dimensions that do not earn a column but matter when diagnosing one call.
     * `query_features` records which capabilities the call used — never the values it
     * passed, which are the partner's data.
     */
    detailsFor(row) {
      return [
        { label: 'Resource', value: row.resource || '—' },
        { label: 'Action', value: row.action || '—' },
        { label: 'IP', value: row.ip || '—' },
        { label: 'Client', value: row.agent || 'unknown' },
        { label: 'Token', value: row.token_ref ? `${row.token_ref}…` : '—' },
        { label: 'Scope version', value: row.scope_version || '—' },
        { label: 'Request', value: formatBytes(row.request_bytes) },
        { label: 'Response', value: formatBytes(row.response_bytes) },
        {
          label: 'Query features',
          value: (row.query_features || []).join(', ').replace(/_/g, ' ') || 'none',
        },
      ]
    },
    async fetch(append = false) {
      try {
        this.loading = true

        const params = {
          ...this.window,
          limit: 25,
        }

        // The API infers granularity for aggregate views; the log has no buckets.
        delete params.granularity

        if (append && this.nextCursor) params.after = this.nextCursor
        if (this.errorsOnly) params.errors_only = true
        if (this.filters.route) params.route = this.filters.route.trim()
        if (this.filters.method) params.method = this.filters.method
        if (this.filters.outcome) params.outcome = this.filters.outcome
        if (this.filters.min_duration_ms) params.min_duration_ms = this.filters.min_duration_ms

        const request = await this.useJwt().mainService.apiClients.usageLogs(this.clientId, params)
        const payload = get(request, 'data.data', {})

        const rows = payload.data || []
        this.rows = append ? [...this.rows, ...rows] : rows
        this.nextCursor = payload.next_cursor || null
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.usage-logs-table >>> td,
.usage-logs-table >>> th {
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
}
</style>
